import { toast } from 'sonner';

const DEFAULT_MESSAGE = 'Something went wrong. Please try again.';

/**
 * Extracts a user-friendly message from an unknown error.
 * Use this for consistent error messaging across the app.
 */
export function getErrorMessage(error: unknown, fallback: string = DEFAULT_MESSAGE): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const msg = (error as { message: unknown }).message;
    if (typeof msg === 'string' && msg.trim()) return msg;
  }
  if (typeof error === 'string' && error.trim()) {
    return error;
  }
  return fallback;
}

export interface HandleErrorOptions {
  /** Show a toast with the error message (default: true) */
  showToast?: boolean;
}

/**
 * Central error handler for API/page errors: resolves message and optionally shows a toast.
 * Use in catch blocks for consistent UX.
 *
 * @example
 * try {
 *   await createJob(payload);
 *   toast.success('Job created');
 * } catch (e) {
 *   handleApiError(e, 'Failed to create job');
 * }
 */
export function handleApiError(
  error: unknown,
  fallback: string = DEFAULT_MESSAGE,
  options: HandleErrorOptions = {}
): string {
  const { showToast = true } = options;
  const message = getErrorMessage(error, fallback);
  if (showToast) {
    toast.error(message);
  }
  return message;
}
